import { useEffect } from "react";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import useMixpanel from "hooks/useMixpanel";
import useMoEngage from "hooks/useMoEngage";
import { getCustomerData } from "utils/localStorage";

import "firebase/analytics";

import { resetUser as mixpanelResetUser } from "../utils/mixpanel";

let analytics;

/**
 * Unified tracking hook that supports Firebase Analytics, MoEngage, and Mixpanel
 * @returns {Function} A unified tracking function with additional methods
 */
const useTracker = () => {
  const { me } = useSelector((state) => state.app);
  const userData = getCustomerData();
  const trackMoengage = useMoEngage();
  const { track: trackMixpanel, identify: identifyMixpanel } = useMixpanel();

  // Automatically identify user in Mixpanel when hook is initialized
  useEffect(() => {
    if (userData?.phone_number) {

      // Extract relevant user properties to send to Mixpanel
      const userTraits = {
        $name: userData.name || userData.fullName,
        $distinct_id: me?.customerId,
        $phone: userData.phone_number,
        $status: userData.status || "NA",
        $short_code: userData.short_code || "NA",
        // Add any other relevant user properties here
      };

      // Identify the user in Mixpanel
      identifyMixpanel(me?.customerId, userTraits);
    }
  }, []);

  // If user is in ghost mode, return a no-op function
  if (userData?.ghostLogin) {
    return () => {};
  }

  /**
   * Unified tracking function
   * @param {string} event - Event name
   * @param {Object} params - Event parameters
   */
  const tracker = (event = "", params = {}) => {
    // Debug logging in non-production environments
    if (process.env.NODE_ENV !== "production") {
      console.log(event, params);
    }

    // Prepare user data for tracking
    const enrichedParams = {
      ...params,
      customer_id: me?.customerId || "",
      customer_name: me?.fullName || "",
      timestamp: new Date().toISOString(),
    };

    // Track with Firebase Analytics
    if (!analytics) {
      analytics = firebase.analytics();
    }

    analytics.logEvent(event, enrichedParams);

    // Track with MoEngage
    trackMoengage(event, params);

    // Track with Mixpanel
    trackMixpanel(event, enrichedParams);
  };

  /**
   * Identify a user in all analytics platforms
   * @param {string} userId - Unique identifier for the user
   * @param {Object} traits - User traits/properties
   */
  tracker.identifyUser = (userId, traits = {}) => {
    // Identify in Mixpanel directly using the hook
    identifyMixpanel(userId, traits);

    // Firebase Analytics user properties if needed
    if (!analytics) {
      analytics = firebase.analytics();
    }
    analytics.setUserId(userId);
    analytics.setUserProperties(traits);

    // MoEngage identify is handled via the useMoEngage hook when user changes
  };

  /**
   * Reset user identity in all analytics platforms (for logout)
   */
  tracker.resetUser = () => {
    // Reset in Mixpanel
    mixpanelResetUser();

    // Reset in Firebase Analytics
    if (analytics) {
      analytics.setUserId(null);
    }

    // MoEngage reset would be handled elsewhere during logout
  };

  return tracker;
};

export default useTracker;
